import { useEffect, useState } from "react";
import type { Comic } from "@/mocks/comics";

function ComicModal({ comic, onClose }: { comic: Comic; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-foreground-950/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`${comic.title} 详情`}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-ink"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col sm:flex-row">
          <div className="relative h-64 w-full shrink-0 sm:h-auto sm:w-56">
            <img src={comic.cover} alt={`${comic.title} 漫画封面`} className="h-full w-full object-cover object-top" />
            <span className="absolute left-3 top-3 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50">
              {comic.status}
            </span>
          </div>
          <div className="flex-1 p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-heading text-xl font-black text-foreground-950">{comic.title}</h3>
              <button onClick={onClose} aria-label="关闭" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200">
                <i className="ri-close-line"></i>
              </button>
            </div>
            <p className="mt-1 text-sm text-foreground-500">
              {comic.category} · {comic.chapters} · {comic.views} 人气
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {comic.tags.map((t) => (
                <span key={t} className="whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{t}</span>
              ))}
            </div>
            <p className="mt-3 text-sm leading-relaxed text-foreground-700">{comic.description}</p>
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center gap-1 text-sm text-accent-600">
                <i className="ri-star-fill"></i>
                <span className="font-semibold">{comic.rating}</span>
                <span className="text-foreground-500">分</span>
              </div>
              <button className="flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600">
                <i className="ri-book-2-line"></i>
                阅读《{comic.title}》
              </button>
            </div>
          </div>
        </div>
        <div className="border-t border-background-200 bg-background-100 p-5">
          <h4 className="font-heading text-sm font-bold text-foreground-950">最新章节</h4>
          <ul className="mt-3 grid grid-cols-2 gap-2 text-sm text-foreground-700 sm:grid-cols-3">
            <li className="rounded-md bg-background-50 px-3 py-2">{comic.latest.includes("更新") ? comic.latest : "番外篇·一"} </li>
            <li className="rounded-md bg-background-50 px-3 py-2">第 6 卷 · 终章</li>
            <li className="rounded-md bg-background-50 px-3 py-2">番外 · 后日谈</li>
          </ul>
          <p className="mt-3 text-xs text-foreground-500">本页为作品预览，完整章节与后续更新请进入阅读页查看。</p>
        </div>
      </div>
    </div>
  );
}

export default function ComicCard({ comic }: { comic: Comic }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <article className="group cursor-pointer" onClick={() => setOpen(true)}>
        <div className="relative aspect-[480/680] overflow-hidden rounded-lg bg-background-200">
          <img
            src={comic.cover}
            alt={`${comic.title} 漫画封面`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <span className="absolute left-2 top-2 rounded-full bg-foreground-950/80 px-2.5 py-0.5 text-xs text-background-50 backdrop-blur">
            {comic.status}
          </span>
          <div className="absolute inset-0 flex items-end justify-center bg-gradient-to-t from-foreground-950/80 via-foreground-950/20 to-transparent p-3 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <span className="flex items-center gap-1.5 rounded-full bg-primary-500 px-4 py-1.5 text-sm text-background-50">
              <i className="ri-book-2-line"></i>查看详情
            </span>
          </div>
        </div>
        <h3 className="mt-2.5 truncate font-heading text-sm font-bold text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
          {comic.title}
        </h3>
        <div className="mt-1 flex items-center justify-between text-xs text-foreground-500">
          <span className="truncate">{comic.chapters}</span>
          <span className="flex items-center gap-0.5 whitespace-nowrap text-accent-600">
            <i className="ri-star-fill text-[10px]"></i>
            {comic.rating}
          </span>
        </div>
      </article>
      {open && <ComicModal comic={comic} onClose={() => setOpen(false)} />}
    </>
  );
}